import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import {
  loginApi,
  logoutApi,
  getCurrentUser,
  signupApi,
  forgotPasswordApi,
  resetPasswordApi
} from "@/api/auth";
import { LoginParams, LoginUser, ApiError } from "@/api/types";
import {
  type SignupRequest,
  type ForgotPasswordRequest,
  type ResetPasswordRequest
} from "@/api/schemas";
import { useAuth } from "@/contexts/auth-context";
import { toast } from "sonner";

interface LoginResponse {
  user: LoginUser;
  jwt: string;
}

export function useLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  return useMutation<LoginResponse, ApiError, LoginParams>({
    mutationFn: async (credentials) => {
      // First do the login to get the JWT
      const loginResponse = await loginApi(credentials);
      // Then fetch the full user details
      // const fullUser = await getCurrentUser();
      return {
        user: loginResponse.user,
        jwt: loginResponse.jwt
      };
    },
    onSuccess: async (data) => {
      localStorage.setItem("jwt", data.jwt);
      const fullUser = await getCurrentUser();
      setUser(fullUser);
      posthog.identify(
        fullUser.id.toString(),
        { email: fullUser.email, coach_viewer: fullUser.coach_viewer }
      );
      navigate("/dashboard");
      toast.success("Welcome back!");
    },
    onError: (error) => {
      toast.error(error.message || "Invalid credentials");
    },
  });
}

export function useSignup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  return useMutation<LoginResponse, ApiError, SignupRequest>({
    mutationFn: async (userData) => {
      const signupResponse = await signupApi(userData);
      return {
        user: signupResponse.user,
        jwt: signupResponse.jwt
      };
    },
    onSuccess: async (data) => {
      localStorage.setItem("jwt", data.jwt);
      const fullUser = await getCurrentUser();
      setUser(fullUser);
      posthog.identify(
        fullUser.id.toString(),
        { email: fullUser.email, coach_viewer: fullUser.coach_viewer }
      );
      navigate("/dashboard");
      toast.success("Welcome! Your account has been created with a free license.");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create account");
    },
  });
}

export function useForgotPassword() {
  return useMutation<{ message: string }, ApiError, ForgotPasswordRequest>({
    mutationFn: forgotPasswordApi,
    onSuccess: (data) => {
      toast.success(data.message);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send reset email");
    },
  });
}

export function useResetPassword() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  return useMutation<LoginResponse, ApiError, ResetPasswordRequest>({
    mutationFn: async (data) => {
      const resetResponse = await resetPasswordApi(data);
      return {
        user: resetResponse.user,
        jwt: resetResponse.jwt
      };
    },
    onSuccess: async (data) => {
      localStorage.setItem("jwt", data.jwt);
      const fullUser = await getCurrentUser();
      setUser(fullUser);
      posthog.identify(
        fullUser.id.toString(),
        { email: fullUser.email, coach_viewer: fullUser.coach_viewer }
      );
      navigate("/dashboard");
      toast.success("Password reset successful! You are now signed in.");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to reset password");
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => logoutApi(),
    onSuccess: () => {
      // Clear all React Query caches
      queryClient.clear();
      localStorage.removeItem("jwt");
      setUser(null);
      navigate("/login");
      toast.success("Successfully logged out");
    },
    onError: (error) => {
      // Also clear caches on error for security
      queryClient.clear();
      toast.error(error.message || "Something went wrong");
      localStorage.removeItem("jwt");
      setUser(null);
      navigate("/login");
    },
  });
}
